import { useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  PlusIcon, XMarkIcon,
} from '@heroicons/react/24/outline';
import api from '../../services/api';
import DateTimeInput from './DateTimeInput';
import SortableList, { Grip, ItemHeader, moveItem } from './SortableList';
import SearchableSelect from './SearchableSelect';
import {
  useAdminPeople, useAdminQuest, useAwardTask, useDeleteQuest, usePublishQuest, useSaveQuest,
} from '../../hooks/useQuestAdmin';
import { CATEGORY_OPTIONS, fmtDate } from './questShared';
import {
  AdminPage, BTN, BTN_DANGER, BTN_SECONDARY, Card, ErrorNote, INPUT, LABEL, Loading, StateBadge,
} from './adminUi';
import SceneEditor, { blankScene, resizePhoto } from './SceneEditor';

// Create or edit a Quest (brief section 9): name, description, category, dates,
// points, and for a quiz its questions, correct answers, pass mark and attempt
// limit. Spot the Risk has scenes instead of questions (SceneEditor.jsx).
// Saving keeps it a draft; publishing is the separate, deliberate step that
// shows it to everyone. Once anyone has taken a quiz its questions lock —
// changing them would mark people against a quiz they never saw.

const pad = (n) => String(n).padStart(2, '0');
// The server's ISO time -> the 'yyyy-mm-ddTHH:mm' a datetime-local input wants.
const toLocalInput = (d) => {
  if (!d) return '';
  const x = new Date(d);
  return `${x.getFullYear()}-${pad(x.getMonth() + 1)}-${pad(x.getDate())}T${pad(x.getHours())}:${pad(x.getMinutes())}`;
};
const fromLocalInput = (s) => (s ? new Date(s).toISOString() : null);

const blankQuestion = () => ({
  kind: 'choice', prompt: '', options: ['', ''], correct: [], image_url: '', video_url: '', explanation: '',
});

// A video: uploaded here (MP4, WebM or MOV up to 200 MB, kept on the tech
// server and stored as 'upload:<name>'), or a YouTube / Vimeo / video file
// link. An upload shows a preview with a way to take it off; a link is typed.
const UPLOADED = /^upload:([A-Za-z0-9_-]{20,40}\.(mp4|webm|mov))$/;
const uploadedSrc = (v) => {
  const m = UPLOADED.exec(v || '');
  return m ? `${process.env.REACT_APP_API_BASE_URL || ''}/api/quest-admin/videos/${m[1]}` : null;
};

function VideoField({ label, value, onChange, disabled = false }) {
  const [progress, setProgress] = useState(null);   // 0-100 while uploading
  const [error, setError] = useState(null);
  const src = uploadedSrc(value);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (file.size > 200 * 1024 * 1024) { setError('Videos can be up to 200 MB.'); return; }
    const fd = new FormData();
    fd.append('file', file);
    try {
      setError(null);
      setProgress(0);
      const { data } = await api.post('/api/quest-admin/videos', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (p) => setProgress(p.total ? Math.round((p.loaded / p.total) * 100) : null),
      });
      onChange(data.video_url);
    } catch (err) {
      setError(err.response?.data?.error || 'That video did not upload.');
    } finally { setProgress(null); }
  };

  return (
    <div>
      <span className={LABEL}>{label}</span>
      {src ? (
        <div className="flex flex-wrap items-start gap-3">
          <video src={src} controls preload="metadata" className="h-32 rounded-md bg-black" />
          {!disabled ? (
            <button type="button" onClick={() => onChange('')} className={BTN_SECONDARY}>Remove video</button>
          ) : null}
        </div>
      ) : (
        <div className="flex gap-2">
          <input value={value || ''} onChange={(e) => onChange(e.target.value)} disabled={disabled || progress !== null}
            placeholder="https://www.youtube.com/watch?v=… or upload" className={INPUT} />
          {!disabled ? (
            <label className={`${BTN_SECONDARY} shrink-0 ${progress !== null ? 'pointer-events-none opacity-60' : 'cursor-pointer'}`}>
              {progress !== null ? `Uploading ${progress}%` : 'Upload'}
              <input type="file" accept="video/mp4,video/webm,video/quicktime" onChange={upload} className="hidden" />
            </label>
          ) : null}
        </div>
      )}
      {error ? <p className="mt-1 text-xs text-rose-600">{error}</p> : null}
    </div>
  );
}

// A question's picture: uploaded (shrunk here first, as Spot the Risk photos
// are) or a web address; and its video, always a web address — YouTube,
// Vimeo or a video file — as videos are too big to upload into a Quest.
function QuestionMedia({ q, locked, set }) {
  const [error, setError] = useState(null);
  const uploaded = (q.image_url || '').startsWith('data:');
  const upload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    try {
      setError(null);
      set({ image_url: await resizePhoto(file) });
    } catch (err) { setError(err.message); }
  };
  return (
    <div className="mt-3 grid gap-3 sm:grid-cols-2">
      <div>
        <span className={LABEL}>Picture (optional)</span>
        {uploaded ? (
          <div className="flex items-center gap-3">
            <img src={q.image_url} alt="" className="h-16 w-24 rounded border border-gray-200 object-cover" />
            {!locked ? (
              <button type="button" onClick={() => set({ image_url: '' })} className={BTN_SECONDARY}>Remove</button>
            ) : null}
          </div>
        ) : (
          <div className="flex gap-2">
            <input value={q.image_url || ''} onChange={(e) => set({ image_url: e.target.value })} disabled={locked}
              placeholder="https://… or upload" className={INPUT} />
            {!locked ? (
              <label className={`${BTN_SECONDARY} cursor-pointer`}>
                Upload
                <input type="file" accept="image/*" onChange={upload} className="hidden" />
              </label>
            ) : null}
          </div>
        )}
        {error ? <p className="mt-1 text-xs text-rose-600">{error}</p> : null}
      </div>
      <VideoField label="Video (optional — upload one, or a YouTube, Vimeo or video file link)"
        value={q.video_url || ''} onChange={(v) => set({ video_url: v })} disabled={locked} />
    </div>
  );
}

// A quiz question's kinds — its "question type", chosen per question so one
// quiz can mix them (not to be mixed up with the Quest type above it). Order:
// options are the steps in the right order (the app shuffles them). Match:
// options[i] pairs with correct[i]. [kind, name, what the employee does]
const KINDS = [
  ['choice', 'Multiple choice', 'Pick the right answer'],
  ['order', 'Put in order', 'Drag the steps into order'],
  ['match', 'Match up', 'Pair each item with its partner'],
];
// A question switched to another type keeps what has been typed: the answers'
// text carries over — a multiple-choice answer becomes a step to order, or the
// left side of a pair, and back — padded to the least that type needs. Only
// what cannot carry over starts empty (which answers were ticked, the right
// side of each pair); switching back to a type already filled in brings all of
// that back (QuestionEditor keeps it).
const MIN_ANSWERS = { choice: 2, order: 3, match: 2 };
function carryOver(q, kind) {
  const texts = (q.options || []).filter((o) => String(o).trim());
  const options = [...texts, ...Array(Math.max(0, MIN_ANSWERS[kind] - texts.length)).fill('')];
  return { options, correct: kind === 'match' ? options.map(() => '') : [] };
}

function defaults() {
  const start = new Date();
  start.setHours(9, 0, 0, 0);
  const end = new Date(start);
  end.setDate(end.getDate() + 30);
  end.setHours(17, 0, 0, 0);
  return {
    title: '',
    description: '',
    category: 'technical',
    quest_type: 'quiz',
    points: 100,
    pass_mark: 70,
    attempt_limit: 3,
    draw_count: '',
    starts_at: toLocalInput(start),
    ends_at: toLocalInput(end),
    video_url: '',
    questions: [{ ...blankQuestion(), _k: 'new-first' }],
  };
}

const fromQuest = (q) => ({
  title: q.title,
  description: q.description || '',
  category: q.category,
  quest_type: q.quest_type,
  points: q.points,
  pass_mark: q.pass_mark ?? '',
  attempt_limit: q.attempt_limit ?? '',
  draw_count: q.draw_count ?? '',
  starts_at: toLocalInput(q.starts_at),
  ends_at: toLocalInput(q.ends_at),
  // ids go back with the save, so the server keeps each question (a go's deal
  // and every attempt point at them)
  questions: q.questions.map((x) => (x.hazards ? {
    id: x.id,
    prompt: x.prompt,
    image_url: x.image_url,
    hazards: x.hazards,
    explanation: x.explanation || '',
  } : {
    id: x.id,
    kind: x.kind || 'choice',
    prompt: x.prompt,
    options: x.options,
    correct: x.correct,
    image_url: x.image_url || '',
    video_url: x.video_url || '',
    explanation: x.explanation || '',
  })),
  video_url: q.video_url || '',
});

// The server marks these; a task Quest is awarded by Comms.
const TYPES = [
  ['quiz', 'Quiz', 'questions, each its own type'],
  ['spot', 'Spot the Risk', 'tap the hazards on a photo'],
  ['task', 'Task', 'Comms award it'],
  ['nominate', 'Values in Action', 'colleagues nominate'],
];
// A question or scene not saved yet has no id, so it gets a key of its own to
// be dragged and opened by (never sent to the server).
let nextKey = 1;
const fresh = (q) => ({ ...q, _k: `new-${nextKey++}` });
const keyOf = (q) => q.id ?? q._k;
const blankFor = (type) => fresh(type === 'spot' ? blankScene() : blankQuestion());

// Put in order: the steps, dragged into the right order. That order is the
// answer; the app shuffles them for the player.
function OrderFields({ q, locked, set }) {
  const setStep = (i, text) => set({ options: q.options.map((o, j) => (j === i ? text : o)) });
  return (
    <>
      <p className={`${LABEL} mt-3`}>Steps — drag them into the right order (players get them shuffled)</p>
      <SortableList items={q.options} keyOf={(o, i) => i} disabled={locked}
        onMove={(from, to) => set({ options: moveItem(q.options, from, to) })}>
        {(o, i, handle) => (
          <div className="flex items-center gap-2 rounded-lg border border-gray-200 bg-white p-1.5">
            {!locked ? <Grip {...handle} /> : null}
            <span className="w-5 text-center text-sm font-bold text-gray-400">{i + 1}</span>
            <input value={o} onChange={(e) => setStep(i, e.target.value)} disabled={locked}
              placeholder={`Step ${i + 1}`} className={`${INPUT} border-transparent bg-gray-50`} />
            {!locked && q.options.length > 3 ? (
              <button type="button" aria-label="Remove step" onClick={() => set({ options: q.options.filter((_, j) => j !== i) })}
                className="rounded p-1.5 text-gray-400 hover:bg-gray-100"><XMarkIcon className="h-4 w-4" /></button>
            ) : null}
          </div>
        )}
      </SortableList>
      {!locked && q.options.length < 8 ? (
        <button type="button" onClick={() => set({ options: [...q.options, ''] })}
          className="mt-2 text-sm font-semibold text-brand hover:text-brand-dark">+ Add a step</button>
      ) : null}
    </>
  );
}

// Match up: pairs. The app shows the left side in order and shuffles the right.
function MatchFields({ q, locked, set }) {
  const setSide = (side, i, text) => set({ [side]: q[side].map((o, j) => (j === i ? text : o)) });
  const remove = (i) => set({ options: q.options.filter((_, j) => j !== i), correct: q.correct.filter((_, j) => j !== i) });
  return (
    <>
      <p className={`${LABEL} mt-3`}>Pairs — each left goes with the right next to it</p>
      <ul className="space-y-2">
        {q.options.map((o, i) => (
          <li key={i} className="flex items-center gap-2">
            <input value={o} onChange={(e) => setSide('options', i, e.target.value)} disabled={locked}
              placeholder="e.g. ONT" className={INPUT} />
            <span className="text-gray-400">→</span>
            <input value={q.correct[i]} onChange={(e) => setSide('correct', i, e.target.value)} disabled={locked}
              placeholder="e.g. The box in the customer's home" className={INPUT} />
            {!locked && q.options.length > 2 ? (
              <button type="button" aria-label="Remove pair" onClick={() => remove(i)}
                className="rounded p-1.5 text-gray-400 hover:bg-gray-100"><XMarkIcon className="h-4 w-4" /></button>
            ) : null}
          </li>
        ))}
      </ul>
      {!locked && q.options.length < 8 ? (
        <button type="button" onClick={() => set({ options: [...q.options, ''], correct: [...q.correct, ''] })}
          className="mt-2 text-sm font-semibold text-brand hover:text-brand-dark">+ Add a pair</button>
      ) : null}
    </>
  );
}

const KIND_TAG = { choice: 'Choice', order: 'In order', match: 'Match up' };

// what is missing, for the one-line view, so a gap shows without opening it
function questionProblem(q) {
  const kind = q.kind || 'choice';
  if (!q.prompt?.trim()) return null;   // "Not written yet" says it
  if (kind === 'choice' && !q.correct.length) return 'no right answer ticked';
  if (q.options.some((o) => !String(o).trim()) || (kind === 'match' && q.correct.some((o) => !String(o).trim()))) {
    return 'something left blank';
  }
  return null;
}

function QuestionEditor({ q, index, locked, onChange, onRemove, handle, open, onToggle }) {
  const set = (patch) => onChange({ ...q, ...patch });
  const kind = q.kind || 'choice';
  const setOption = (i, text) => set({ options: q.options.map((o, j) => (j === i ? text : o)) });
  const toggleCorrect = (i) => set({ correct: q.correct.includes(i) ? q.correct.filter((c) => c !== i) : [...q.correct, i] });
  const removeOption = (i) => set({
    options: q.options.filter((_, j) => j !== i),
    correct: q.correct.filter((c) => c !== i).map((c) => (c > i ? c - 1 : c)),
  });
  // each type's answers as last left, so switching back loses nothing
  const byType = useRef({});
  const switchType = (k) => {
    if (k === kind) return;
    byType.current[kind] = { options: q.options, correct: q.correct };
    set({ kind: k, ...(byType.current[k] || carryOver(q, k)) });
  };

  return (
    <Card className="px-3 py-2">
      <ItemHeader handle={handle} locked={locked} open={open} onToggle={onToggle} onRemove={onRemove}
        number={index + 1} tag={KIND_TAG[kind]} title={q.prompt} problem={questionProblem(q)} />
      {open ? (
      <div className="px-1 pb-2 pt-3">
      {!locked ? (
        <div className="mb-3">
          <span className={LABEL}>Question type <span className="normal-case tracking-normal text-gray-400">— for this question only; what you have typed carries over</span></span>
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-3" role="radiogroup" aria-label="Question type">
            {KINDS.map(([k, label, hint]) => (
              <button key={k} type="button" role="radio" aria-checked={kind === k}
                onClick={() => switchType(k)}
                className={`flex items-start gap-2 rounded-lg border px-3 py-2 text-left ${
                  kind === k ? 'border-brand bg-brand/5' : 'border-gray-200 bg-white hover:border-gray-300'}`}>
                <span className={`mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full border ${
                  kind === k ? 'border-brand' : 'border-gray-300'}`}>
                  {kind === k ? <span className="h-2 w-2 rounded-full bg-brand" /> : null}
                </span>
                <span className="min-w-0">
                  <span className={`block text-sm font-semibold ${kind === k ? 'text-brand-dark' : 'text-gray-800'}`}>{label}</span>
                  <span className="block text-xs text-gray-500">{hint}</span>
                </span>
              </button>
            ))}
          </div>
        </div>
      ) : null}
      <textarea value={q.prompt} onChange={(e) => set({ prompt: e.target.value })} disabled={locked} rows={2}
        placeholder={kind === 'order' ? 'e.g. Put the steps of a fibre install in order'
          : kind === 'match' ? 'e.g. Match each term to what it means' : 'The question'}
        className={INPUT} />

      {kind === 'order' ? <OrderFields q={q} locked={locked} set={set} /> : null}
      {kind === 'match' ? <MatchFields q={q} locked={locked} set={set} /> : null}
      {kind === 'choice' ? (
        <>
          <p className={`${LABEL} mt-3`}>Answers — tick every correct one</p>
          <ul className="space-y-2">
            {q.options.map((o, i) => (
              <li key={i} className="flex items-center gap-2">
                <input type="checkbox" checked={q.correct.includes(i)} onChange={() => toggleCorrect(i)} disabled={locked}
                  aria-label={`Answer ${i + 1} is correct`} className="h-5 w-5 rounded border-gray-300 text-brand focus:ring-brand" />
                <input value={o} onChange={(e) => setOption(i, e.target.value)} disabled={locked}
                  placeholder={`Answer ${i + 1}`} className={INPUT} />
                {!locked && q.options.length > 2 ? (
                  <button type="button" aria-label="Remove answer" onClick={() => removeOption(i)}
                    className="rounded p-1.5 text-gray-400 hover:bg-gray-100"><XMarkIcon className="h-4 w-4" /></button>
                ) : null}
              </li>
            ))}
          </ul>
          {!locked && q.options.length < 8 ? (
            <button type="button" onClick={() => set({ options: [...q.options, ''] })}
              className="mt-2 text-sm font-semibold text-brand hover:text-brand-dark">+ Add an answer</button>
          ) : null}
        </>
      ) : null}

      <QuestionMedia q={q} locked={locked} set={set} />
      <label className="mt-3 block">
        <span className={LABEL}>Explanation (shown after they finish)</span>
        <input value={q.explanation} onChange={(e) => set({ explanation: e.target.value })} disabled={locked}
          className={INPUT} />
      </label>
      </div>
      ) : null}
    </Card>
  );
}

// Task Quests are judged: Comms say who completed them.
function AwardTask({ quest }) {
  const people = useAdminPeople();
  const award = useAwardTask();
  const [who, setWho] = useState('');
  const done = new Set(quest.completions.map((c) => c.entra_user_id));

  return (
    <Card>
      <p className="border-b border-gray-100 px-4 py-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
        Who has completed it ({quest.completions.length})
      </p>
      {quest.published ? (
        <div className="flex flex-wrap items-end gap-2 p-4">
          <div className="min-w-[240px] flex-1">
            <SearchableSelect value={who} onChange={setWho} placeholder="Pick a person"
              options={(people.data || []).filter((p) => !done.has(p.entra_user_id))
                .map((p) => ({ value: p.entra_user_id, label: p.display_name }))} />
          </div>
          <button type="button" disabled={!who || award.isPending} className={BTN}
            onClick={async () => { try { await award.mutateAsync({ id: quest.id, entraUserId: who }); setWho(''); } catch { /* below */ } }}>
            Award {quest.points} points
          </button>
          <div className="w-full"><ErrorNote error={award.error} /></div>
        </div>
      ) : <p className="px-4 py-3 text-sm text-gray-500">Publish the Quest before awarding it.</p>}
      <ul className="divide-y divide-gray-100">
        {quest.completions.map((c) => (
          <li key={c.entra_user_id} className="flex justify-between px-4 py-2 text-sm">
            <span className="text-gray-900">{c.display_name || 'Unknown'}</span>
            <span className="text-gray-400">{fmtDate(c.completed_at)} · +{c.points_awarded}</span>
          </li>
        ))}
      </ul>
    </Card>
  );
}

function EditorForm({ quest }) {
  const navigate = useNavigate();
  const save = useSaveQuest();
  const publish = usePublishQuest();
  const remove = useDeleteQuest();
  const [form, setForm] = useState(() => (quest ? fromQuest(quest) : defaults()));
  const [saved, setSaved] = useState(false);
  // Quiz questions show in full; a Spot the Risk pool (dozens of photos)
  // starts folded to one line each. Any of them folds or opens on its own, and
  // one button does them all.
  const [folded, setFolded] = useState(() => new Set(
    quest && quest.quest_type === 'spot' ? quest.questions.map((x) => x.id) : [],
  ));
  const isOpen = (k) => !folded.has(k);
  const toggle = (k) => setFolded((f) => { const n = new Set(f); if (n.has(k)) n.delete(k); else n.add(k); return n; });
  const set = (patch) => { setForm((f) => ({ ...f, ...patch })); setSaved(false); };
  // Once people have played, any question can still be changed or removed:
  // the server keeps the version each person answered for their results, and
  // everyone who plays from then on gets the new one.
  const played = Boolean(quest?.questions_locked);
  const isQuiz = form.quest_type === 'quiz';
  const isSpot = form.quest_type === 'spot';
  const isMarked = isQuiz || isSpot;
  // A quiz question and a scene have nothing in common, so another type starts
  // with its own questions — but each type's are kept as they were left, so
  // switching back (a misclick, say) loses nothing.
  const questionsByType = useRef({});
  const setType = (type) => {
    if (type === form.quest_type) return;
    questionsByType.current[form.quest_type] = form.questions;
    const first = type === 'quiz' || type === 'spot' ? [blankFor(type)] : [];
    set({ quest_type: type, questions: questionsByType.current[type] || first });
    setFolded(new Set());
  };

  const setQuestion = (i, q) => set({ questions: form.questions.map((x, j) => (j === i ? q : x)) });
  const addQuestion = () => {
    const q = blankFor(form.quest_type);
    set({ questions: [...form.questions, q] });
  };

  const onSave = async (e) => {
    e.preventDefault();
    try {
      const body = {
        ...form,
        id: quest?.id,
        starts_at: fromLocalInput(form.starts_at),
        ends_at: fromLocalInput(form.ends_at),
        questions: isMarked ? form.questions.map(({ _k, ...q }) => q) : [],
      };
      const result = await save.mutateAsync(body);
      setSaved(true);
      if (!quest) navigate(`/quest/quests/${result.id}`, { replace: true });
    } catch { /* shown below */ }
  };

  const onDelete = async () => {
    if (!window.confirm('Delete this Quest? This cannot be undone.')) return;
    try {
      await remove.mutateAsync({ id: quest.id });
      navigate('/quest', { replace: true });
    } catch { /* shown below */ }
  };

  return (
    <div className="space-y-4">
      <form onSubmit={onSave} className="space-y-4">
        {quest ? (
          <Card className="flex flex-wrap items-center justify-between gap-3 px-4 py-3">
            <span className="flex items-center gap-2 text-sm text-gray-600">
              <StateBadge state={quest.state} />
              {quest.published ? 'Everyone can see this Quest.' : 'Only admins can see this draft.'}
              {quest.participants ? ` ${quest.participants} taken part.` : ''}
            </span>
            <span className="flex flex-wrap gap-2">
              <button type="button" disabled={publish.isPending}
                onClick={() => publish.mutate({ id: quest.id, published: !quest.published })}
                className={quest.published ? BTN_SECONDARY : BTN}>
                {quest.published ? 'Unpublish' : 'Publish'}
              </button>
              {!quest.participants ? (
                <button type="button" onClick={onDelete} disabled={remove.isPending} className={BTN_DANGER}>Delete</button>
              ) : null}
            </span>
            <div className="w-full space-y-2 empty:hidden">
              {publish.isError ? <ErrorNote error={publish.error} /> : null}
              {remove.isError ? <ErrorNote error={remove.error} /> : null}
            </div>
          </Card>
        ) : null}

        {/* The Quest's settings, tight and lined up: name, description, its
            type in one row, then category / points / dates on one grid line,
            and a marked Quest's settings on the line under. */}
        <Card className="space-y-3 p-4">
          <label className="block">
            <span className={LABEL}>Quest name</span>
            <input value={form.title} onChange={(e) => set({ title: e.target.value })} maxLength={160}
              placeholder="e.g. Fibre Challenge" className={INPUT} />
          </label>
          <label className="block">
            <span className={LABEL}>Description (what employees read first)</span>
            <textarea value={form.description} onChange={(e) => set({ description: e.target.value })} rows={2}
              placeholder="e.g. Can you spot all five hazards?" className={INPUT} />
          </label>
          <div>
            <span className={LABEL}>Quest type</span>
            <div className="grid grid-cols-2 gap-1 rounded-lg bg-gray-100 p-1 sm:grid-cols-4">
              {TYPES.map(([k, label, hint]) => (
                <button key={k} type="button" disabled={Boolean(quest?.participants)} onClick={() => setType(k)} title={hint}
                  className={`rounded-md px-2 py-1.5 text-sm font-semibold leading-tight ${
                    form.quest_type === k ? 'bg-white text-brand-dark shadow' : 'text-gray-600 hover:text-gray-900'}`}>
                  {label}
                  <span className="block text-[11px] font-normal text-gray-500">{hint}</span>
                </button>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
            <label className="block">
              <span className={LABEL}>Category</span>
              <select value={form.category} onChange={(e) => set({ category: e.target.value })} className={INPUT}>
                {CATEGORY_OPTIONS.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
              </select>
            </label>
            <label className="block" title={form.quest_type === 'nominate' ? 'Points per approved nomination, paid to the colleague' : undefined}>
              <span className={LABEL}>{form.quest_type === 'nominate' ? 'Points per nomination' : 'Points'}</span>
              <input type="number" min="1" value={form.points} onChange={(e) => set({ points: e.target.value })} className={INPUT} />
            </label>
            <div>
              <span className={LABEL}>Opens</span>
              <DateTimeInput value={form.starts_at} onChange={(v) => set({ starts_at: v })} />
            </div>
            <div>
              <span className={LABEL}>Closes</span>
              <DateTimeInput value={form.ends_at} onChange={(v) => set({ ends_at: v })} min={form.starts_at} />
            </div>
            {isMarked ? (
              <>
                <label className="block">
                  <span className={LABEL}>Pass mark %</span>
                  <input type="number" min="0" max="100" value={form.pass_mark} onChange={(e) => set({ pass_mark: e.target.value })}
                    placeholder="none" className={INPUT} />
                </label>
                <label className="block">
                  <span className={LABEL}>Attempts</span>
                  <input type="number" min="1" max="20" value={form.attempt_limit} onChange={(e) => set({ attempt_limit: e.target.value })}
                    placeholder="unlimited" className={INPUT} />
                </label>
                <label className="block" title="Each go is dealt this many at random from the pool below, preferring ones the person has not seen. Blank = all of them.">
                  <span className={LABEL}>{isSpot ? 'Scenes per go' : 'Questions per go'}</span>
                  <input type="number" min="1" max="50" value={form.draw_count} onChange={(e) => set({ draw_count: e.target.value })}
                    placeholder="all" className={INPUT} />
                </label>
              </>
            ) : null}
          </div>
          <div>
            <VideoField label="Video (optional)" value={form.video_url} onChange={(v) => set({ video_url: v })} />
            <p className="mt-1 text-xs text-gray-400">Shown before they start. Upload one, or paste a YouTube, Vimeo or video file link.</p>
          </div>
        </Card>

        {isMarked ? (
          <div className="space-y-3">
            <div className="flex flex-wrap items-baseline justify-between gap-2">
              <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-500">
                {isSpot ? 'Scenes' : 'Questions'} — the pool ({form.questions.length})
              </h2>
              <p className="text-sm text-gray-500">
                {form.draw_count && Number(form.draw_count) < form.questions.length
                  ? `Each go gets ${form.draw_count} of these at random, so every go is a different game.`
                  : `Every go gets all of them. Set "${isSpot ? 'Scenes' : 'Questions'} per go" to deal a different set each time.`}
              </p>
            </div>
            {played ? (
              <p className="rounded-md border border-sky-200 bg-sky-50 px-3 py-2 text-sm text-sky-800">
                People have already played this Quest. You can still change, add or remove {isSpot ? 'scenes' : 'questions'} —
                anyone who has answered keeps their result exactly as they answered it, and everyone who plays from
                now on gets the new version.
              </p>
            ) : null}
            <div className="flex flex-wrap items-center justify-between gap-2">
              <p className="text-xs text-gray-400">
                Drag <span className="font-semibold">⠿</span> to reorder. Click a {isSpot ? 'scene' : 'question'}'s heading to fold or open it.
              </p>
              {form.questions.length > 1 ? (
                <button type="button" className="text-xs font-semibold text-brand hover:text-brand-dark"
                  onClick={() => setFolded(folded.size ? new Set() : new Set(form.questions.map(keyOf)))}>
                  {folded.size ? 'Expand all' : 'Collapse all'}
                </button>
              ) : null}
            </div>
            <SortableList items={form.questions} keyOf={keyOf}
              onMove={(from, to) => set({ questions: moveItem(form.questions, from, to) })}>
              {(q, i, handle) => {
                const Editor = isSpot ? SceneEditor : QuestionEditor;
                return (
                  <Editor
                    q={q}
                    index={i}
                    locked={false}
                    handle={handle}
                    open={isOpen(keyOf(q))}
                    onToggle={() => toggle(keyOf(q))}
                    onChange={(next) => setQuestion(i, next)}
                    onRemove={() => set({ questions: form.questions.filter((_, j) => j !== i) })}
                  />
                );
              }}
            </SortableList>
            <button type="button" onClick={addQuestion} className={BTN_SECONDARY}>
              <PlusIcon className="h-4 w-4" /> {isSpot ? 'Add a scene' : 'Add a question'}
            </button>
          </div>
        ) : null}

        <div className="sticky bottom-4 z-10 flex flex-wrap items-center gap-3 rounded-lg border border-gray-200 bg-white/95 px-4 py-3 shadow">
          <button type="submit" disabled={save.isPending} className={BTN}>
            {save.isPending ? 'Saving…' : quest ? 'Save changes' : 'Create draft'}
          </button>
          {saved && !save.isPending ? <span className="text-sm text-emerald-700">Saved.</span> : null}
          <div className="w-full empty:hidden">{save.isError ? <ErrorNote error={save.error} /> : null}</div>
        </div>
      </form>

      {/* Outside the form: Enter in its search box mustn't save the Quest */}
      {quest && quest.quest_type === 'task' ? <AwardTask quest={quest} /> : null}
    </div>
  );
}

function QuestEditor() {
  const { id } = useParams();
  const isNew = id === 'new';
  const q = useAdminQuest(isNew ? null : id);

  return (
    <AdminPage title={isNew ? 'New Quest' : (q.data?.title || 'Quest')}>
      {!isNew ? <Loading q={q} what="this Quest" /> : null}
      {isNew ? <EditorForm /> : q.data ? <EditorForm key={q.data.id} quest={q.data} /> : null}
    </AdminPage>
  );
}

export default QuestEditor;
